import {
  useState, useMemo 
} from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { toast } from 'sonner';
import {
  useUsers,
  useInviteUser,
  useUserAction,
  useUpdateUser,
  useDeleteUser,
  useSites,
  useLanguages,
  type UserStatus,
  type SiteStatus,
  type InviteUserRequest,
  type UpdateUserRequest,
  type UserAction,
} from '@/features/settings';
import { TABS } from './settings-constants';
import { OrganizationView } from './organization-view';
import { UsersView } from './users-view';
import { SitesView } from './sites-view';

export function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // User filters
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<UserStatus | undefined>(undefined);
  
  // Site filters
  const [siteStatusFilter, setSiteStatusFilter] = useState<SiteStatus | undefined>(undefined);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [siteFormData, setSiteFormData] = useState({
    name: '',
    code: '',
    type: 'ship',
    timezone: '',
    currency: 'USD',
  });

  const activeTab = useMemo(() => {
    if (location.pathname.includes('/sites')) return 'sites';
    if (location.pathname.includes('/users')) return 'users';
    return 'organization';
  }, [location.pathname]);

  // Data fetching
  const {
    data: usersData,
    isLoading: usersLoading,
    refetch: refetchUsers,
    isFetching: usersFetching,
  } = useUsers({
    search: userSearch || undefined,
    status: userStatusFilter,
  });

  const {
    data: sitesData,
    isLoading: sitesLoading,
    refetch: refetchSites,
  } = useSites({ status: siteStatusFilter });

  const {
    data: languagesData,
    isLoading: languagesLoading,
  } = useLanguages();

  // Mutations
  const inviteUserMutation = useInviteUser();
  const userActionMutation = useUserAction();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const users = usersData?.data ?? [];
  const sites = sitesData?.data ?? [];
  const languages = languagesData?.data ?? [];

  // Stats
  const userStats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    pending: users.filter((u) => u.status === 'pending').length,
    disabled: users.filter((u) => u.status === 'disabled').length,
  }), [users]);

  const siteStats = useMemo(() => ({
    total: sites.length,
    active: sites.filter((s) => s.status === 'active').length,
    totalVenues: sites.reduce((sum, s) => sum + s.venues.length, 0),
  }), [sites]);

  const langStats = useMemo(() => ({
    total: languages.length,
    enabled: languages.filter((l) => l.isEnabled).length,
    avgProgress: languages.length
      ? Math.round(languages.reduce((sum, l) => sum + l.translationProgress, 0) / languages.length)
      : 0,
  }), [languages]);

  // User handlers
  const handleInviteUser = async (data: InviteUserRequest) => {
    try {
      await inviteUserMutation.mutateAsync(data);
      toast.success(`Invitation sent to ${data.email}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send invitation');
      throw error;
    }
  };

  const handleUpdateUser = async (userId: string, data: UpdateUserRequest) => {
    try {
      await updateUserMutation.mutateAsync({
        userId,
        data 
      });
      toast.success('User updated successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update user');
      throw error;
    }
  };

  const handleUserAction = async (userId: string, action: UserAction | 'delete') => {
    try {
      if (action === 'delete') {
        await deleteUserMutation.mutateAsync(userId);
        toast.success('User deleted');
      } else {
        const result = await userActionMutation.mutateAsync({
          userId,
          action 
        });
        toast.success(result.message ?? 'Action completed');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Action failed');
      throw error;
    }
  };

  // Site handlers
  const handleAddSite = () => {
    setSiteFormData({
      name: '',
      code: '',
      type: 'ship',
      timezone: '',
      currency: 'USD',
    });
    setShowSiteModal(true);
  };

  const handleSaveSite = () => {
    if (!siteFormData.name || !siteFormData.code || !siteFormData.timezone) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success(`Site ${siteFormData.name} created`);
    setShowSiteModal(false);
    refetchSites();
  };

  const handleDeleteSites = () => {
    toast.success(`Deleted ${selectedSites.length} site(s)`);
    setSelectedSites([]);
  };

  const toggleSiteSelection = (siteId: string) => {
    setSelectedSites((prev) =>
      prev.includes(siteId) ? prev.filter((id) => id !== siteId) : [...prev, siteId]
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">Manage users, sites, and system configuration</p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'organization' && (
        <OrganizationView
          languages={languages}
          isLoading={languagesLoading}
          langStats={langStats}
        />
      )}

      {activeTab === 'users' && (
        <UsersView
          users={users}
          isLoading={usersLoading}
          userStats={userStats}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          userStatusFilter={userStatusFilter}
          setUserStatusFilter={setUserStatusFilter}
          onInviteUser={handleInviteUser}
          onUpdateUser={handleUpdateUser}
          onUserAction={handleUserAction}
          onRefresh={() => refetchUsers()}
          isFetching={usersFetching}
        />
      )}

      {activeTab === 'sites' && (
        <SitesView
          sites={sites}
          isLoading={sitesLoading}
          siteStats={siteStats}
          siteStatusFilter={siteStatusFilter}
          setSiteStatusFilter={setSiteStatusFilter}
          selectedSites={selectedSites}
          toggleSiteSelection={toggleSiteSelection}
          showSiteModal={showSiteModal}
          setShowSiteModal={setShowSiteModal}
          siteFormData={siteFormData}
          setSiteFormData={setSiteFormData}
          onAddSite={handleAddSite}
          onSaveSite={handleSaveSite}
          onDeleteSites={handleDeleteSites}
        />
      )}
    </div>
  );
}
